/* ═══════════════════════════════════════════════════════════════
   TaskSubtasks — Task Sheet's Subtasks section. Read for anyone who can
   see the task; add/toggle gated on the task's own edit right
   (Decision 12).
   ═══════════════════════════════════════════════════════════════ */
import { useState } from 'react';
import type { Task } from '../../../../shared/types';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Checkbox } from '../../../components/ui/checkbox';
import EmptyState from '../../../components/EmptyState';
import { Icon } from '../../../components/ui/Icon';
import { rem } from '../../../lib/rem';
import { X, ListChecks } from 'lucide-react';

export interface TaskSubtasksProps {
  subtasks: Task[];
  canEdit: boolean;
  onAdd: (title: string) => Promise<void>;
  onToggle: (sub: Task) => Promise<void>;
}

const sectionHeaderStyle = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  color: 'var(--tm)',
  textTransform: 'uppercase' as const,
  letterSpacing: '.05em',
};

export function TaskSubtasks({ subtasks, canEdit, onAdd, onToggle }: TaskSubtasksProps) {
  const [showAddSubtask, setShowAddSubtask] = useState(false);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');

  async function handleAddSubtask() {
    if (!newSubtaskTitle.trim()) return;
    await onAdd(newSubtaskTitle);
    setNewSubtaskTitle('');
    setShowAddSubtask(false);
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: rem(6),
        }}
      >
        <span style={sectionHeaderStyle}>
          Subtasks{' '}
          {subtasks.length > 0
            ? `(${subtasks.filter((s) => s.status === 'done').length}/${subtasks.length})`
            : ''}
        </span>
        {canEdit && (
          <Button variant="ghost" size="sm" onClick={() => setShowAddSubtask(true)}>
            + Add
          </Button>
        )}
      </div>

      {subtasks.length === 0 && !showAddSubtask && (
        <EmptyState compact icon={<Icon icon={ListChecks} size="md" />} title="No subtasks" />
      )}

      {subtasks.map((sub) => (
        <div
          key={sub.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: `var(--space-1) 0`,
            borderBottom: '1px solid var(--bd)',
          }}
        >
          <Checkbox
            checked={sub.status === 'done'}
            onCheckedChange={() => void onToggle(sub)}
            disabled={!canEdit}
            aria-label={sub.title}
          />
          <span
            style={{
              fontSize: 'var(--font-size-base)',
              color: sub.status === 'done' ? 'var(--tm)' : 'var(--tx)',
              textDecoration: sub.status === 'done' ? 'line-through' : 'none',
              flex: 1,
            }}
          >
            {sub.title}
          </span>
        </div>
      ))}

      {showAddSubtask && canEdit && (
        <div style={{ display: 'flex', gap: rem(6), marginTop: rem(6) }}>
          <Input
            value={newSubtaskTitle}
            onChange={(e) => setNewSubtaskTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddSubtask()}
            placeholder="Subtask title..."
            autoFocus
            style={{ flex: 1 }}
          />
          <Button variant="secondary" size="sm" onClick={handleAddSubtask}>
            Add
          </Button>
          <Button
            variant="outline"
            size="sm"
            aria-label="Cancel"
            onClick={() => {
              setShowAddSubtask(false);
              setNewSubtaskTitle('');
            }}
          >
            <Icon icon={X} em={1.4} />
          </Button>
        </div>
      )}
    </div>
  );
}
